import { useCallback, useMemo } from 'react'
import { GuidedTour, type GuidedTourStep } from '@/components/business/guided-tour'
import {
  NOTIFICATION_GUIDED_TOUR_STORAGE_KEY,
  setNotificationCenterTourOpen,
} from '@/features/notification/notification-tour-events'

const NOTIFICATION_ENTRY_GUIDED_TOUR_STEPS: GuidedTourStep[] = [
  {
    id: 'notification-bell',
    target: '[data-tour="notification-bell"]',
    title: '查看消息提醒',
    description: '这里会显示咨询会话和系统消息。开启通知后，离开页面也能更及时收到提醒。',
    placement: 'bottom',
  },
  {
    id: 'notification-settings',
    target: '[data-tour="notification-settings-entry"]',
    title: '进入通知设置',
    description: '点击这里进入通知设置，按提示关注服务号或开启网页提醒，减少漏看咨询回复。',
    placement: 'left',
  },
]

interface NotificationGuidedTourProps {
  open: boolean
}

export function NotificationGuidedTour({ open }: NotificationGuidedTourProps) {
  const closeNotificationCenter = useCallback(() => {
    setNotificationCenterTourOpen(false)
  }, [])

  return (
    <GuidedTour
      open={open}
      steps={NOTIFICATION_ENTRY_GUIDED_TOUR_STEPS}
      storageKey={NOTIFICATION_GUIDED_TOUR_STORAGE_KEY}
      onChange={(_, step) => {
        if (step.id === 'notification-settings') {
          setNotificationCenterTourOpen(true)
        }
      }}
      onClose={closeNotificationCenter}
      onFinish={closeNotificationCenter}
      onSkip={closeNotificationCenter}
    />
  )
}

interface NotificationSettingsGuidedTourProps {
  open: boolean
  includeWechatReminder: boolean
  includeBrowserReminder: boolean
}

export function NotificationSettingsGuidedTour({
  open,
  includeWechatReminder,
  includeBrowserReminder,
}: NotificationSettingsGuidedTourProps) {
  const steps = useMemo<GuidedTourStep[]>(() => {
    const nextSteps: GuidedTourStep[] = []

    if (includeWechatReminder) {
      nextSteps.push({
        id: 'notification-offline-reminder',
        target: '[data-tour="notification-offline-reminder"]',
        title: '开启微信离线提醒',
        description: '关注服务号后，网页关闭或离线时也能收到律师回复提醒。',
        placement: 'bottom',
      })
    }

    if (includeBrowserReminder) {
      nextSteps.push({
        id: 'notification-browser-reminder',
        target: '[data-tour="notification-browser-reminder"]',
        title: '开启网页在线提醒',
        description: '允许浏览器通知后，网页在后台时的新咨询消息会额外弹出本地提醒。',
        placement: 'top',
      })
    }

    return nextSteps
  }, [includeBrowserReminder, includeWechatReminder])

  return (
    <GuidedTour
      open={open && steps.length > 0}
      steps={steps}
      storageKey={NOTIFICATION_GUIDED_TOUR_STORAGE_KEY}
    />
  )
}
